import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Header from "../../../component/Header";
import ProgressDialog from "../../../component/ProgressDialog";
import { Toast } from "../../../component/Toast";
import { getCMSByKey, userContactUsAction } from "../../../redux/Actions";
import {
  isEmpty,
  newEmailValidation,
  adminContactNumberValidation,
} from "../../../services/validation";
import { strings } from "../../../utils/constants";
import Flag from "../../../utils/Logo/caneda_flag.png";
import { changeActiveHeader } from "../../../redux/Reducers/GeneralReducer";

const ContactUs = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { language } = useSelector((state) => state.generalReducer);

  const { isUserContactLoading } = useSelector(
    (state) => state.userContactUsReducer
  );

  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const getCMS = async () => {
    setLoading(true);
    try {
      const response = await dispatch(
        getCMSByKey({ language, key: strings.cmsPages.CONTACT })
      );
      if (response.payload?.success && response.payload?.data) {
        const content = JSON.parse(response.payload.data.content);
        setCmsContent({
          title: response.payload.data.title,
          title_fr: response.payload.data.title_fr,
          address: content.address,
          address_fr: content.address_fr,
          email: content.email,
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(changeActiveHeader(strings.navigationHeader.user.CONTECT_US));
    getCMS();
  }, [language]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (isEmpty(userName)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: t(strings.error.NAME_REQUIRED_ERROR),
      });
    } else if (isEmpty(email)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: t(strings.error.EMAIL_REQUIRED_ERROR),
      });
    } else if (!newEmailValidation(email)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: t(strings.error.EMAIL_VALIDATION_ERROR),
      });
    } else if (adminContactNumberValidation(phone)) {
      if (
        adminContactNumberValidation(phone) ===
        strings.admin.error.PHONE_NUMBER_REQUIRED
      ) {
        Toast({
          type: "error",
          title: strings.words.ERROR,
          message: t(strings.error.PHONE_NUMBER_REQUIRED_ERROR),
        });
      } else {
        Toast({
          type: "error",
          title: strings.words.ERROR,
          message: t(strings.error.PHONE_NUMBER_VALIDATION_ERROR),
        });
      }
    } else if (isEmpty(message)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: t(strings.error.MESSAGE_REQUIRED_ERROR),
      });
    } else {
      let requestInfo = {
        name: userName,
        email: email,
        phone: phone,
        message: message,
      };
      let userContactUsResponse = await dispatch(
        userContactUsAction({ language: language, requestInfo })
      );
      if (userContactUsResponse.payload?.success) {
        setUserName("");
        setEmail("");
        setPhone("");
        setMessage("");
      }
    }
  };

  const nameChangeHandler = (e) => {
    const regex = new RegExp(/^[a-zA-Z0-9 ]*$/);
    if (regex.test(e.target.value)) {
      setUserName(e.target.value);
    }
  };
  const emailChangeHandler = (e) => {
    setEmail(e.target.value);
  };
  const phoneChangeHandler = (e) => {
    const regex = new RegExp(/^[0-9]*$/);
    if (e.target.value.length <= 10 && regex.test(e.target.value)) {
      setPhone(e.target.value);
    } else {
      return;
    }
  };
  const messageChangeHandler = (e) => {
    setMessage(e.target.value);
  };

  const getTitle = () => {
    if (cmsContent) {
      return language === strings.language.FR
        ? cmsContent.title_fr || cmsContent.title
        : cmsContent.title;
    }
    return t(strings.navigationHeader.user.CONTECT_US);
  };

  const getAddress = () => {
    if (cmsContent) {
      const address =
        language === strings.language.FR
          ? cmsContent.address_fr || cmsContent.address
          : cmsContent.address;
      return address ? address.split(",") : [];
    }
    return [];
  };

  const getEmail = () => {
    return cmsContent?.email || "";
  };

  return (
    <body>
      <Header />
      <section className="subplan-banner bg--primary">
        <div className="container">
          <h1 className="font-lato_bold font-26 fg-white text-center py-5 m-0">
            {getTitle()}
          </h1>
        </div>
      </section>
      <section className="py-5 billing-sec">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">
                  {t(strings.translate.PLEASE_WAIT)}
                </span>
              </div>
            </div>
          ) : (
            <div className="row flex-column-reverse flex-md-row justify-content-center g-lg-5">
              {/* Left Column: Contact Information */}
              <div className="col-md-6 col-lg-5 fg-gray-30 pe-lg-5 mb-4 mb-md-0">
                <div className="mb-5">
                  <h4 className="fg-primary font-lato_bold mb-3">{t(strings.words.ADDRESS)}</h4>
                  <p className="lh-lg mb-0 fs-6">
                    {getAddress().map((data, index) => (
                      <React.Fragment key={index}>
                        <span>{data}</span> <br />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
                
                <div className="pt-4 border-top">
                  <p className="mb-2">{t(strings.translate.CONTECT_US_TEXT)}</p>
                  <a
                    href={"mailto:" + getEmail()}
                    className="text-decoration-none fg-primary font-lato_bold fs-5 d-inline-block"
                  >
                    {getEmail()}
                  </a>
                </div>
              </div>

              {/* Right Column: Contact Form */}
              <div className="col-md-6 col-lg-5">
                <div className="mb-4">
                  <label htmlFor="name" className="form-label mb-1 fw-bold small fg-gray-30 text-uppercase">
                    {t(strings.words.NAME)} *
                  </label>
                  <input
                    type="text"
                    className="form-control py-2"
                    id="name"
                    placeholder={t(strings.translate.YOUR_NAME)}
                    autoComplete="off"
                    autoFocus
                    value={userName}
                    maxLength={150}
                    onChange={nameChangeHandler}
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="email" className="form-label mb-1 fw-bold small fg-gray-30 text-uppercase">
                    {t(strings.words.EMAIL)} *
                  </label>
                  <input
                    type="email"
                    className="form-control py-2"
                    id="email"
                    placeholder={t(strings.translate.YOUR_EMAIL)}
                    autoComplete="off"
                    value={email}
                    onChange={emailChangeHandler}
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="phoneNumber" className="form-label mb-1 fw-bold small fg-gray-30 text-uppercase">
                    {t(strings.translate.PHONE_NUMBER)} *
                  </label>
                  <div className="d-flex gap-2 align-items-stretch">
                    <div className="form-control py-2 d-flex align-items-center" style={{ width: "fit-content" }}>
                      <img src={Flag} alt="flag" width={22} height={14} />
                      <span className="ms-2 small fw-bold">+1</span>
                    </div>
                    <input
                      type="text"
                      className="form-control py-2 flex-grow-1"
                      id="phoneNumber"
                      placeholder="Phone Number"
                      value={phone}
                      maxLength={10}
                      onChange={phoneChangeHandler}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="message" className="form-label mb-1 fw-bold small fg-gray-30 text-uppercase">
                    {strings.words.MESSAGE} *
                  </label>
                  <textarea
                    id="message"
                    placeholder={t(strings.translate.YOUR_MESSAGE)}
                    className="form-control py-2"
                    rows="4"
                    autoComplete="off"
                    value={message}
                    maxLength={255}
                    onChange={messageChangeHandler}
                  ></textarea>
                </div>
                <div className="mb-2">
                  <button
                    className="btn btn--primary text-uppercase w-100 py-2 font-lato_bold"
                    onClick={submitHandler}
                    type="button"
                  >
                    {t(strings.words.SUBMIT)}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      {isUserContactLoading && (
        <ProgressDialog open={isUserContactLoading} isAdmin={false} />
      )}
    </body>
  );
};

export default ContactUs;
